import { isNovaEnv } from '@repo/constants'
import { FEATURE_FLAGS_ENV } from '@repo/feature-flags'

const base = {
  logoutRedirectQuery: 'post_logout_redirect_uri',
  guideBasePath: '/guide/',
}

const local = {
  ...base,
  env: 'local',
  envTag: 'LOCAL',
  backendURL: 'http://localhost:5000',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.LOCAL,
}

const dev = {
  ...base,
  env: 'development',
  envTag: 'DEV',
  backendURL: 'https://fuze-dev-api.v7apps.com',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.DEV,
}

type ConfigVars = typeof dev

const qa: ConfigVars = {
  ...base,
  env: 'qa',
  envTag: 'QA',
  backendURL: 'https://fuze-qa-api.v7apps.com',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.QA,
}

const devNova: ConfigVars = {
  ...base,
  env: 'dev-nova',
  envTag: 'DEV-NOVA',
  backendURL: 'https://fuze-dev-nova-api.v7apps.com',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.DEV_NOVA,
}

/**
 * The Nova variant against a local backend and a local copy of a Nova database. Same as `local`
 * apart from the env name, which is what turns the Nova switch on, and the Nova feature-flag set so
 * CRT is off as it is on the deployed Nova environments.
 */
const localNova: ConfigVars = {
  ...base,
  env: 'local-nova',
  envTag: 'LOCAL-NOVA',
  backendURL: 'http://localhost:5000',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.DEV_NOVA,
}

const qaNova: ConfigVars = {
  ...base,
  env: 'qa-nova',
  envTag: 'QA-NOVA',
  backendURL: 'https://fuze-qa-nova-api.v7apps.com',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.QA_NOVA,
}

const pp: ConfigVars = {
  ...base,
  env: 'pp',
  envTag: 'PRE-PROD',
  backendURL: 'https://fuze-preprod-api.v7apps.com',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.PP,
}

const prod: ConfigVars = {
  ...base,
  env: 'production',
  envTag: 'PROD',
  backendURL: 'https://api.fuze-app.ca',
  logoutUrl:
    'https://login.microsoftonline.com/ff9c7474-421d-4957-8d47-c4b64dec87b5/oauth2/v2.0/logout',
  featureFlags: FEATURE_FLAGS_ENV.PROD,
}

const ENV_CONFIGS: Record<string, ConfigVars> = {
  production: prod,
  pp,
  'qa-nova': qaNova,
  qa,
  'dev-nova': devNova,
  dev,
  'local-nova': localNova,
  // `pnpm dev` runs `dotenv -e ../../.env -- vite`, and the root .env sets this to `development`.
  // It is a local machine, not the deployed dev environment, so it resolves to the local config.
  development: local,
}

/**
 * Unset means a local build. A value that is set but unrecognised is a deployment mistake, so it
 * throws rather than quietly picking something.
 */
function getEnvConfig() {
  const name = import.meta.env.VITE_FRONTEND_ENV

  if (!name) return local

  const found = ENV_CONFIGS[name]
  if (!found) {
    throw new Error(
      `Unknown VITE_FRONTEND_ENV "${name}". Expected one of: ${Object.keys(ENV_CONFIGS).join(', ')}.`,
    )
  }

  return found
}

const config = getEnvConfig()
export default config

/** Whether this build is Nova. Derived from the environment name, never set independently. */
export const isNova = isNovaEnv(config.env)
